import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { ModuleStringsProvider } from '@platform/modularity/ModuleStringsProvider';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { NewRecordDialog } from '../form/NewRecordDialog';
import { adHocEntryOf, presetLabelOf } from '../menu/adHocEntry';
import { buildMenu, findEntry } from '../menu/buildMenu';
import { filterLabelOf } from '../menu/filterLabelOf';
import { mainRecordTypeOf, tableEntryKeyOf } from '../menu/moduleSections';
import { useMenuFacets } from '../menu/useMenuFacets';
import { useAdvancedDsl } from './advanced/useAdvancedDsl';
import { useAdvancedFilter } from './advanced/useAdvancedFilter';
import { useQuickFields } from './advanced/useQuickFields';
import { BulkDialog } from './BulkDialog';
import { hasActiveFilters, RESET_FILTERS } from './filters/activeFilters';
import { customTabViewsOf } from './filters/tabViews';
import { PageFrame } from './header/PageFrame';
import { RecordListHead } from './header/RecordListHead';
import { ViewPageHead } from './header/ViewPageHead';
import { tableTitle } from './header/tableTitle';
import { useModuleCrumbs } from './header/useModuleCrumbs';
import type { IRecordListBodyProps } from './IRecordListBodyProps';
import { isCustomView } from './isCustomView';
import { ignoresMenuFilter } from './listHeading';
import { RecordListContent } from './RecordListContent';
import { useSavedView } from './savedViews/useSavedView';
import { useViewSettings } from './savedViews/useViewSettings';
import { ViewSettingsDrawer } from './savedViews/ViewSettingsDrawer';
import { useNewRecordPreset } from './create/useNewRecordPreset';
import { emptyStateOf } from './emptyState';
import { useCreateButton } from './toolbar/useCreateButton';
import { useCapability } from './toolbar/useCapability';
import { DeleteSelectedDialog } from './toolbar/DeleteSelectedDialog';
import { useCanExport } from './toolbar/useCanExport';
import { useListActions } from './toolbar/useListActions';
import { useRecordListModel } from './useRecordListModel';
import { useViewLabel } from './useViewLabel';

/**
 * Сторінка таблиці модуля за прототипом v4: крихти, шапка (заголовок «Модуль — Таблиця», рядок фільтрів, розширені фільтри, панель дій)
 * і тіло, що прокручується (смуга вибраного, подання, пагінація); діалоги створення, масової зміни статусу й видалення, панель
 * «Налаштування подання». Пункт меню
 * (`?item=`) дає фільтр і пресет (пресет поза меню — з порад: `?item=preset:unassigned`, adHocEntryOf); пункт із власним фільтром
 * показується чипом, × відкриває пункт «Усі записи» типу (tableEntryKeyOf).
 * Подання, що вантажать дані самі (власні аналітичні подання пакета, панель), — окремі подання (UIUX views/view-switcher): шапка ViewPageHead із
 * «Модуль — Таблиця» над заголовком подання, лише фільтри, теги й пошук — без вкладок подань, збережених подань і дій над записами. Власне
 * подання записів («Бібліотека» бази знань, customTabViewsOf) — як у прототипі: звичайна шапка списку, лише без вкладок, поки воно відкрите.
 */
export function RecordListBody({ ws, module, rt, routeView }: IRecordListBodyProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const strings = useModuleStrings(module.module.key);
  const viewLabel = useViewLabel(module);
  const [params] = useSearchParams();
  const facets = useMenuFacets(ws, rt);
  const moduleName = strings.moduleName(module.module.name);
  const sections = useMemo(() => buildMenu(rt, facets, strings, moduleName), [rt, facets, strings, moduleName]);
  const options = useMemo(() => buildMenu(rt, {}, strings, moduleName), [rt, strings, moduleName]);
  const itemKey = params.get('item') ?? tableEntryKeyOf(rt);
  const presetLabel = (p: string) => presetLabelOf(p, rt, strings, (k) => (i18n.exists(`menu.presets.${k}`) ? t(`menu.presets.${k}`) : undefined));
  const entry = findEntry(sections, itemKey, options, adHocEntryOf(itemKey, presetLabel, (f) => filterLabelOf(f, rt, strings)));
  const model = useRecordListModel(ws, module, rt, entry, routeView, { skipPage: ignoresMenuFilter });
  const saved = useSavedView(ws, rt.key, model.state.savedViewId)?.definition;
  const { actions, confirm } = useListActions(model, entry, saved);
  const settings = useViewSettings(model);
  const typeName = strings.entity(rt.key, 'name', rt.name);
  const childTail = useMemo(() => (rt.key === mainRecordTypeOf(module)?.key ? [] : [{ label: typeName }]), [rt.key, module, typeName]);
  // дочірня таблиця: модуль-власник — нове відкриття модуля (прототип v4 openModule(pmC)), а не повернення
  const crumbs = useModuleCrumbs(module.module.key, moduleName, childTail, childTail.length ? routes.module(module.module.key) : undefined);
  const canExport = useCanExport(ws);
  const canCreate = useCapability(ws, 'edit');
  const advanced = useAdvancedDsl(ws, rt.key);
  const [, setConditions] = useAdvancedFilter(ws, rt.key);
  const [, setQuick] = useQuickFields(ws, rt.key);
  const view = model.state.view;
  const preset = useNewRecordPreset();
  const title = tableTitle(moduleName, typeName);
  const separate = (isCustomView(view) && !customTabViewsOf(rt).includes(view)) || view === 'dashboard';
  const create = useCreateButton(model, actions);
  const createText = create.text;
  const filtered = Boolean(entry.preset || entry.filter);
  const narrowed = hasActiveFilters(model.state) || Boolean(advanced);
  const reset = () => { model.setState(RESET_FILTERS); setConditions([]); setQuick({}); };
  const state = emptyStateOf({ narrowed, filtered, item: entry.label, create: createText, canCreate }, t);
  const empty = { text: state.text, actionText: state.actionText, onAction: state.action === 'reset' ? reset : state.action === 'create' ? actions.create : undefined };
  const description = strings.entity(rt.key, 'description', rt.description) || strings.tagline(module.module.tagline);
  const head = separate ? <ViewPageHead model={model} eyebrow={title} title={viewLabel(view)} description={description} /> : (
    <RecordListHead model={model} actions={actions} createText={createText} createKinds={create.kinds} views={{ onEdit: settings.onEdit, onNew: settings.onNew }}
      title={title} description={description}
      item={filtered ? { label: entry.label, onClear: () => navigate(routes.list(module.module.key, rt.key, undefined, tableEntryKeyOf(rt))) } : undefined} />
  );
  return (
    <ModuleStringsProvider strings={strings}>
      <PageFrame crumbs={crumbs} head={head}>
        <RecordListContent model={model} actions={actions} canExport={canExport} unfiltered={ignoresMenuFilter(view)} empty={empty} />
      </PageFrame>
      <NewRecordDialog key={JSON.stringify(preset)} ws={ws} module={module} rt={rt} initialValues={preset ?? undefined} />
      {!isCustomView(view) && <BulkDialog model={model} />}
      {confirm.open && <DeleteSelectedDialog count={confirm.count} busy={actions.busy} onCancel={confirm.cancel} onConfirm={() => void confirm.run()} />}
      {settings.open && (
        <ViewSettingsDrawer ws={ws} rt={rt} view={settings.open.view} initial={settings.open.initial} onClose={settings.close}
          onSaved={settings.onSaved} onDeleted={settings.onDeleted} />
      )}
    </ModuleStringsProvider>
  );
}
